import React from 'react';
import { X, Share, PlusSquare, MoreVertical, Download } from 'lucide-react';

interface PWAInstallModalProps {
  isOpen: boolean;
  onClose: () => void;
  organizationName?: string;
}

export function PWAInstallModal({ isOpen, onClose, organizationName }: PWAInstallModalProps) {
  if (!isOpen) return null;

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isChrome = /Chrome/.test(navigator.userAgent) && !/Edge/.test(navigator.userAgent);
  const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);

  const displayName = organizationName || 'this app';

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[85vh] overflow-y-auto shadow-2xl animate-slide-up">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">
            Install {displayName}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          <p className="text-gray-600 dark:text-gray-400 text-sm">
            Install {displayName} to your home screen for quick access. It works offline and feels like a native app!
          </p>

          {/* iOS Safari Instructions */}
          {isIOS && isSafari && (
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900 dark:text-white">
                Follow these steps:
              </h3>
              
              <div className="space-y-4">
                {/* Step 1 */}
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-theme-primary to-theme-secondary flex items-center justify-center text-white font-bold text-sm">
                    1
                  </div>
                  <div className="flex-1 pt-1">
                    <p className="text-gray-900 dark:text-white font-medium">
                      Tap the Share button
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      Look for <Share className="inline w-4 h-4 mx-1 text-blue-500" /> at the bottom of Safari
                    </p>
                  </div>
                </div>

                {/* Step 2 */}
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-theme-primary to-theme-secondary flex items-center justify-center text-white font-bold text-sm">
                    2
                  </div>
                  <div className="flex-1 pt-1">
                    <p className="text-gray-900 dark:text-white font-medium">
                      Scroll down and tap "Add to Home Screen"
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      Look for <PlusSquare className="inline w-4 h-4 mx-1 text-gray-600" /> in the share menu
                    </p>
                  </div>
                </div>

                {/* Step 3 */}
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-theme-primary to-theme-secondary flex items-center justify-center text-white font-bold text-sm">
                    3
                  </div>
                  <div className="flex-1 pt-1">
                    <p className="text-gray-900 dark:text-white font-medium">
                      Tap "Add" to confirm
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      The app icon will appear on your home screen
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* iOS Chrome Instructions */}
          {isIOS && isChrome && (
            <div className="space-y-4">
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                <p className="text-amber-800 dark:text-amber-200 text-sm">
                  <strong>Note:</strong> For the best experience, please open this page in Safari and then add to home screen.
                </p>
              </div>
              
              <h3 className="font-semibold text-gray-900 dark:text-white">
                To add from Safari:
              </h3>
              
              <ol className="list-decimal list-inside space-y-2 text-gray-700 dark:text-gray-300 text-sm">
                <li>Copy the current URL</li>
                <li>Open Safari and paste the URL</li>
                <li>Tap the Share button <Share className="inline w-4 h-4 mx-1" /></li>
                <li>Select "Add to Home Screen"</li>
              </ol>
            </div>
          )}

          {/* Android Instructions (fallback) */}
          {!isIOS && (
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900 dark:text-white">
                Follow these steps:
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-theme-primary to-theme-secondary flex items-center justify-center text-white font-bold text-sm">
                    1
                  </div>
                  <div className="flex-1 pt-1">
                    <p className="text-gray-900 dark:text-white font-medium">
                      Tap the menu button
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      Look for <MoreVertical className="inline w-4 h-4 mx-1" /> at the top right
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-theme-primary to-theme-secondary flex items-center justify-center text-white font-bold text-sm">
                    2
                  </div>
                  <div className="flex-1 pt-1">
                    <p className="text-gray-900 dark:text-white font-medium">
                      Tap "Add to Home screen" or "Install app"
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      <Download className="inline w-4 h-4 mr-1" /> The option may vary by browser
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white dark:bg-gray-800 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="w-full py-3 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white font-medium rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}
